import Big from 'big.js';
import type React from 'react';
import { useCallback, useRef } from 'react';
import { useRifm } from 'rifm';

import { DECIMAL } from '../../../tokens/number';
import { format, parseNumber } from '../../../utils/number';

const REGEX = RegExp(`((?=^-)|[^0-9${DECIMAL}])`, 'g');
const POSITIVE_REGEX = RegExp(`[^0-9${DECIMAL}]`, 'g');
const INTEGER_REGEX = RegExp(`((?=^-)|[^0-9])`, 'g');
const POSITIVE_INTEGER_REGEX = RegExp(`[^0-9]`, 'g');

const FORMAT_OPTIONS = {
  spec: '0',
  round: false,
  pretty: true,
};

/**
 * This hook is used to control a well-formatted (with comma auto-formatting) number input.
 * It uses the rifm library to format the input and handle keyboard events.
 * @returns The props to pass to the NumberInput component
 */
export function useNumberInput({
  /** The value of the input - this is always to be controlled by the parent */
  value,
  onChange,
  min,
  max,
  minIncrement = '0.01',
  /** The default increment of the input, for up/down arrow keys */
  defaultIncrement = '0.1',
  /** The large increment of the input, used when shift is held down */
  largeIncrement = '1',
  /** Whether the input should only allow integer (non-decimal) inputs */
  integerOnly = false,
  ...props
}: {
  value?: string | null;
  min?: string;
  max?: string;
  minIncrement?: string;
  defaultIncrement?: string;
  largeIncrement?: string;
  integerOnly?: boolean;
  onChange: (value: string) => void;
}) {
  const ref = useRef<HTMLInputElement | null>(null);

  const onValueChange = useCallback(
    (formattedValue: string) => {
      const regex = integerOnly
        ? min != null && min !== '' && Big(min).gte('0')
          ? POSITIVE_INTEGER_REGEX
          : INTEGER_REGEX
        : min != null && min !== '' && Big(min).gte('0')
          ? POSITIVE_REGEX
          : REGEX;
      onChange(formattedValue.replace(regex, ''));
    },
    [onChange, integerOnly, min]
  );

  const internalFormat = useCallback(
    (value: string) => {
      if (integerOnly) {
        const regex = min != null && min !== '' && Big(min).gte('0') ? POSITIVE_INTEGER_REGEX : INTEGER_REGEX;
        const cleanValue = value.replace(regex, '');

        if (cleanValue === '-') {
          return '-';
        } else if (cleanValue.startsWith('-')) {
          const sign = Big(cleanValue).lte(0) && Big(cleanValue).gt(-1) ? '-' : '';
          return `${sign}${format(cleanValue, FORMAT_OPTIONS)}`;
        } else if (cleanValue && Big(cleanValue).eq(0)) {
          return cleanValue;
        } else {
          return format(cleanValue, FORMAT_OPTIONS);
        }
      }

      // Original decimal logic
      const regex = min != null && min !== '' && Big(min).gte('0') ? POSITIVE_REGEX : REGEX;
      const cleanValue = restrictToOneDecimal(value.replace(regex, ''));
      if (cleanValue === DECIMAL) {
        return DECIMAL;
      } else if (cleanValue === '-') {
        return '-';
      } else if (cleanValue === `-${DECIMAL}`) {
        return `-${DECIMAL}`;
      } else if (value.includes(DECIMAL)) {
        if (value.endsWith(DECIMAL)) {
          const isNegative = value.startsWith('-');
          const absCleanValue = cleanValue.startsWith('-') ? cleanValue.slice(1) : cleanValue;

          const formattedValue = format(absCleanValue, FORMAT_OPTIONS);
          const sign = isNegative ? '-' : '';
          return restrictToOneDecimal(`${sign}${formattedValue}${DECIMAL}`);
        }
        const [integers, decimals] = cleanValue.split(DECIMAL);
        const isNegative = value.startsWith('-');
        const absIntegers = integers.startsWith('-') ? integers.slice(1) : integers;

        const formattedIntegers = format(absIntegers, { ...FORMAT_OPTIONS, spec: '0' });
        const sign = isNegative ? '-' : '';
        return restrictToOneDecimal(`${sign}${formattedIntegers}${DECIMAL}${decimals}`);
      } else if (cleanValue.startsWith('-')) {
        const isNegative = value.startsWith('-');
        const absCleanValue = cleanValue.startsWith('-') ? cleanValue.slice(1) : cleanValue;
        const sign = isNegative ? '-' : '';
        return `${sign}${format(absCleanValue, FORMAT_OPTIONS)}`;
      } else if (cleanValue && Big(cleanValue).eq(0)) {
        return cleanValue;
      } else {
        return format(cleanValue, FORMAT_OPTIONS);
      }
    },
    [min, integerOnly]
  );

  const rifm = useRifm({
    value: value ?? '',
    onChange: onValueChange,
    accept: integerOnly ? /[\d-]+/g : /[\d.-]+/g,
    format: internalFormat,
    replace: (value: string) => {
      if (integerOnly) {
        return clampValue(value, min, max);
      }

      if (value === DECIMAL) {
        setTimeout(() => ref.current?.setSelectionRange(2, 2), 0);
        return `0${DECIMAL}`;
      } else if (value === '-') {
        // We seem to catch the replace action one render cycle behind sometimes
        // Causing input "-" and then '12345' very fast to have weird cursor selection
        // -123|45
        // If we set the selectionRange to something comically large, it'd ensure the cursor is at the end.
        setTimeout(() => ref.current?.setSelectionRange(100, 100), 0);
        return '-';
      } else if (value.startsWith(`-${DECIMAL}`)) {
        const parts = value.split(DECIMAL);
        const decimals = parts[1];
        const newValue = `-0${DECIMAL}${decimals}`;
        setTimeout(() => ref.current?.setSelectionRange(newValue.length, newValue.length), 0);
        return newValue;
      }

      return clampValue(value, min, max);
    },
  });

  const onKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      const { target, key, altKey, shiftKey } = e;
      const input = target as HTMLInputElement;
      if (key === 'ArrowDown' || key === 'ArrowUp') {
        e.preventDefault();
        const value = Big(parseNumber(input.value) || '0');
        const increment = shiftKey ? largeIncrement : altKey ? minIncrement : defaultIncrement;
        const newValue = key === 'ArrowDown' ? value.sub(increment) : value.add(increment);
        // If min is defined, and new value is less than min, set value to min.
        if (min !== undefined && newValue.lt(min)) {
          onValueChange(format(min, FORMAT_OPTIONS));
          return;
        }
        onValueChange(format(clampValue(newValue.toFixed(), min, max), FORMAT_OPTIONS));
      }
    },
    [onValueChange, minIncrement, defaultIncrement, largeIncrement, min, max]
  );
  return { onKeyDown, ref, step: defaultIncrement, ...rifm, ...props };
}

function clampValue(value: string, min?: string, max?: string) {
  const regex = min != null && min !== '' && Big(min).gte('0') ? POSITIVE_REGEX : REGEX;
  const cleanValue = restrictToOneDecimal(value.replace(regex, ''));
  if (value != null && value !== '') {
    if (min != null && min !== '' && Big(cleanValue).lt(min)) {
      return format(min, FORMAT_OPTIONS);
    }
    if (max != null && max !== '' && Big(cleanValue).gt(max)) {
      return format(max, FORMAT_OPTIONS);
    }
  }
  return value;
}

function restrictToOneDecimal(value: string): string {
  if (value.includes(DECIMAL)) {
    const [integers, decimals] = value.split(DECIMAL, 2);
    // Decimals might include additional decimals, lets remove them.
    const cleanDecimals = decimals.replace(DECIMAL, '');
    return `${integers}${DECIMAL}${cleanDecimals}`;
  }
  return value;
}
